import {
  convertImageFileWithCloudinary,
  IMAGE_QUALITY_OPTIONS
} from 'lib/media/imageHelper';
import { getVideoCoverImage } from 'lib/media/videoHelper';
import {
  type MediaType,
  type MediaPin,
  type PinMediaFields,
  type ResolvedPinMedia,
  type ResolvedSlideItem
} from './mediaCarousel.types';

const IMAGE_EXT_REGEX =
  /\.(png|jpe?g|gif|webp|bmp|avif|tiff|heic)(\?.*)?(#.*)?$/i;
const VIDEO_EXT_REGEX = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?(#.*)?$/i;
const PROVIDER_VIDEO_PIN_TYPES = new Set(['youtube/video', 'vimeo/video']);

function isHttpUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function getPinType(pin: PinMediaFields): string | undefined {
  const pinRecord = pin as Record<string, unknown>;
  const pinType = pinRecord.pin_type;
  return typeof pinType === 'string' ? pinType : undefined;
}

function getPinMimeType(pin: PinMediaFields): string | undefined {
  const pinRecord = pin as Record<string, unknown>;
  const fileRecord = pinRecord.file as Record<string, unknown> | undefined;
  const mimeType = fileRecord?.mime_type;
  return typeof mimeType === 'string' ? mimeType : undefined;
}

/** Same field order as v1 pin / MediaCarousel: pin url → resize → file url → original. */
function getPinUrlFieldValues(pin: PinMediaFields): unknown[] {
  const pinRecord = pin as Record<string, unknown>;
  const fileRecord = pinRecord.file as Record<string, unknown> | undefined;
  return [
    pinRecord.url,
    fileRecord?.resize_url,
    fileRecord?.url,
    fileRecord?.original_url
  ];
}

function getPinUrls(pin: PinMediaFields): string[] {
  return getPinUrlFieldValues(pin).filter(isHttpUrl);
}

function isPinOfMediaType(pin: PinMediaFields, mediaType: MediaType): boolean {
  const pinType = getPinType(pin);
  const pinMimeType = getPinMimeType(pin);
  const pinRecord = pin as Record<string, unknown>;
  const pinUrl = pinRecord.url;
  const regex = mediaType === 'image' ? IMAGE_EXT_REGEX : VIDEO_EXT_REGEX;

  if (pinType?.startsWith(`${mediaType}/`)) return true;
  if (pinType === 'file' && pinMimeType?.startsWith(`${mediaType}/`))
    return true;
  if (
    mediaType === 'video' &&
    pinType &&
    PROVIDER_VIDEO_PIN_TYPES.has(pinType)
  ) {
    return true;
  }

  return typeof pinUrl === 'string' ? regex.test(pinUrl) : false;
}

function getPinSrc(pin: PinMediaFields): string | undefined {
  return getPinUrls(pin)[0];
}

function getPinVideoThumbnailSrc(pin: PinMediaFields): string | undefined {
  if (!isPinOfMediaType(pin, 'video')) return undefined;

  const src = getPinSrc(pin);
  if (!src) return undefined;

  const thumbnailSrc = getVideoCoverImage(src);
  return thumbnailSrc || undefined;
}

export function resolvePinMedia(pin: PinMediaFields): ResolvedPinMedia | null {
  if (isPinOfMediaType(pin, 'image')) {
    const candidate = getPinUrlFieldValues(pin).find(
      (v): v is string => typeof v === 'string' && v.trim().length > 0
    );
    if (candidate) {
      const transformed =
        convertImageFileWithCloudinary(
          candidate,
          IMAGE_QUALITY_OPTIONS.medium
        ) || candidate;
      return {
        mediaType: 'image',
        src: transformed,
        ...(transformed !== candidate ? { fallbackSrc: candidate } : {})
      };
    }
  }

  const videoThumbnailSrc = getPinVideoThumbnailSrc(pin);
  if (videoThumbnailSrc) return { mediaType: 'video', src: videoThumbnailSrc };

  return null;
}

export function resolveMediaSlides(pins: MediaPin[]): ResolvedSlideItem[] {
  return [...pins]
    .sort((a, b) => {
      const aOrder =
        typeof a.order === 'number' ? a.order : Number.POSITIVE_INFINITY;
      const bOrder =
        typeof b.order === 'number' ? b.order : Number.POSITIVE_INFINITY;
      return aOrder - bOrder;
    })
    .map((pin, index) => {
      const resolvedMedia = resolvePinMedia(pin);
      if (!resolvedMedia) return null;
      return { pin, index, resolvedMedia };
    })
    .filter((item): item is ResolvedSlideItem => item !== null);
}

export function filterMediaPinsWithSrc(pins: MediaPin[]): MediaPin[] {
  return pins.filter(pin => !!resolvePinMedia(pin));
}
